import { type ReactElement, useEffect, useMemo, useRef, useState } from "react"

import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"

import { ChevronLeft, ChevronRight } from "@mui/icons-material"
import { Grid } from "@mui/material"
import { type BoxPlotTooltipProps, ResponsiveBoxPlot } from "@nivo/boxplot"
import { Typography } from "@synapse-analytics/synapse-ui"

import { CalculateStandardDeviation, generateBoxplotData } from "../../features/Insights/helpers"
import { getTheme } from "../../hooks/UseTheme"
import { NivoTheme } from "../../themes/nivoTheme"
import { annotateNumber, customColorScheme } from "../../utils/genericHelpers"
import { KonanEmptyState } from "../utility/KonanEmptyState"
import type { ChartsContainerProps } from "./ChartsContainer"
import { ChartsContainer } from "./ChartsContainer"

import styles from "./BoxPlot.module.scss"

// Define the props for the BoxPlot component
interface LineGraphProps extends Omit<ChartsContainerProps, "children" | "emptyState"> {
  title: string
  subHeader?: React.ReactElement
  data: Record<
    string,
    | Record<
        string,
        | {
            max: number | null
            min: number | null
            mean: number | null
            count: number
            "25th_percentile": number | null
            "50th_percentile": number | null
            "75th_percentile": number | null
          }
        | undefined
      >
    | undefined
  >
  isLoading: boolean
  graphHeight: number
  selectProps?: {
    options: string[]
    value: string
    setValue: (val: string) => void
    multiple?: boolean
  }
  variant?: "linear" | "time"
  emptyState?: { title: string; subTitle?: string }
  minBarWidth?: number // Minimum width per box for horizontal scrolling
  scrollable?: boolean // Whether the box plot should be scrollable
}

// Define the BoxPlot component
export function BoxPlot(props: Readonly<LineGraphProps>): React.ReactElement {
  const { t } = useTranslation("graphs")

  const {
    data,
    title,
    graphHeight,
    isLoading,
    subHeader,
    selectProps,
    emptyState,
    customHeaderComponent,
    tooltipText,
    minBarWidth = 100, // Default minimum width per box group
    scrollable = false, // Default to scrollable
  } = props

  const theme = getTheme()

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const mousePosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
  const tooltipRef = useRef<HTMLDivElement | null>(null)

  // Constants for layout
  const MARGIN_TOP = 20
  const MARGIN_BOTTOM = 40
  const MARGIN_RIGHT = 0

  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(false)
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Adjust the data for the box plot
  const adjustedData = useMemo(() => {
    const result: Array<{
      value: number
      group: string
      subgroup: string
      mu: number
      n: number
      sd: number
    }> = []

    for (const [pKey, pValue] of Object.entries(data)) {
      // Iterate over each key-value pair in the data object
      if (!pValue) continue
      for (const [key, value] of Object.entries(pValue)) {
        if (value?.count === 0) break

        // Generate box plot data points for each group
        const boxPlotPoints = generateBoxplotData({
          count: value?.count as number,
          max: value?.max as number,
          min: value?.min as number,
          Q1: value?.["25th_percentile"] as number,
          Q3: value?.["75th_percentile"] as number,
        })

        // Calculate the standard deviation once for the boxPlotPoints
        const sd = CalculateStandardDeviation(boxPlotPoints)

        // Map over the boxPlotPoints and generate the adjusted data
        boxPlotPoints.forEach((point) => {
          result.push({
            value: point,
            group: key,
            subgroup: pKey,
            mu: value?.mean as number,
            n: value?.count as number,
            sd: sd,
          })
        })
      }
    }

    return result
  }, [data])

  // Calculate the minimum width needed for horizontal scrolling
  const chartWidth = useMemo(() => {
    if (!adjustedData || adjustedData.length === 0) return 400

    // Get unique groups to calculate width
    const uniqueGroups = [...new Set(adjustedData.map((d) => d.group))]
    const numGroups = uniqueGroups.length

    // Calculate minimum width: boxes + margins (excluding y-axis width)
    const minWidth = numGroups * minBarWidth + MARGIN_RIGHT

    return Math.max(minWidth, 400) // Minimum 400px width
  }, [adjustedData, minBarWidth])

  // Handle scroll events to show/hide arrows
  const handleScroll = (): void => {
    if (!scrollContainerRef.current) return

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current

    setShowLeftArrow(scrollLeft > 0)
    setShowRightArrow(Math.ceil(scrollLeft) < Math.floor(scrollWidth - clientWidth))
  }

  const scrollLeft = (): void => {
    if (!scrollContainerRef.current) return
    scrollContainerRef.current.scrollBy({ left: -200, behavior: "smooth" })
  }

  const scrollRight = (): void => {
    if (!scrollContainerRef.current) return
    scrollContainerRef.current.scrollBy({ left: 200, behavior: "smooth" })
  }

  const startContinuousScroll = (direction: "left" | "right"): void => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current)
    }

    const scrollAmount = direction === "left" ? -10 : 10

    scrollIntervalRef.current = setInterval(() => {
      if (!scrollContainerRef.current) return
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: "auto" })

      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current

      // stop scrolling if at the edges
      if (!(Math.ceil(scrollLeft) < Math.floor(scrollWidth - clientWidth)) || !(scrollLeft > 0)) {
        stopContinuousScroll()
      }
    }, 16) // ~60fps
  }

  const stopContinuousScroll = (): void => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current)
      scrollIntervalRef.current = null
    }
  }

  const handleMouseMove = (e: React.MouseEvent): void => {
    mousePosRef.current = { x: e.clientX, y: e.clientY }
  }

  const CustomTooltip = ({
    formatted: { mean, n, values, quantiles, extrema },
    label,
  }: BoxPlotTooltipProps): ReactElement => {
    const xOffset = tooltipRef.current?.clientWidth ? tooltipRef.current.clientWidth / 2 : 75
    const yOffset = (tooltipRef.current?.clientHeight ? tooltipRef.current.clientHeight : 200) + 10

    return createPortal(
      <div
        className={`chart-tooltip ${styles.tooltip}`}
        ref={tooltipRef}
        style={{
          left: mousePosRef.current.x - xOffset,
          top: mousePosRef.current.y - yOffset,
        }}
      >
        <Typography variant="h3-bold" variantColor={2} gutterBottom>
          {label}
        </Typography>

        <Typography variant="p" variantColor={2} gutterBottom>
          {t("boxPlot.tooltip.pointsCount")} <strong>{n}</strong>
        </Typography>

        <Typography variant="p" variantColor={2}>
          {t("boxPlot.tooltip.mean")} <strong>{mean}</strong>
        </Typography>
        <Typography variant="p" variantColor={2}>
          {t("boxPlot.tooltip.min")} <strong>{extrema[0]}</strong>
        </Typography>
        <Typography variant="p" variantColor={2} gutterBottom>
          {t("boxPlot.tooltip.max")} <strong>{extrema[1]}</strong>
        </Typography>

        {quantiles.map((quantile, index) => {
          return (
            ["25", "50", "75"].includes(quantile) && (
              <Typography variant="p" variantColor={2} key={quantile}>
                {quantile === "25" && t("boxPlot.tooltip.percentile25")}
                {quantile === "50" && t("boxPlot.tooltip.percentile50")}
                {quantile === "75" && t("boxPlot.tooltip.percentile75")}
                <strong>{values[index]}</strong>
              </Typography>
            )
          )
        })}
      </div>,
      document.body,
    )
  }

  // Set up scroll listener and initial arrow state
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return

    // Set initial arrow visibility
    handleScroll()

    scrollContainer.addEventListener("scroll", handleScroll)

    // Also listen for resize events to update arrow visibility
    const resizeObserver = new ResizeObserver(() => {
      handleScroll()
    })
    resizeObserver.observe(scrollContainer)

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll)
      resizeObserver.disconnect()
    }
  }, [adjustedData]) // Added chartWidth as dependency

  // Additional effect to handle immediate arrow visibility updates when chartWidth changes
  useEffect(() => {
    // Use a small timeout to ensure DOM has updated after chartWidth change
    const timeoutId = setTimeout(() => {
      handleScroll()
    }, 0)

    return () => clearTimeout(timeoutId)
  }, [chartWidth])

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current)
      }
    }
  }, [])

  // Render the BoxPlot component
  return (
    <ChartsContainer
      title={title}
      isLoading={isLoading}
      graphHeight={graphHeight + 80}
      selectProps={!emptyState ? selectProps : undefined}
      customHeaderComponent={customHeaderComponent}
      tooltipText={tooltipText}
      gutterBottom
    >
      {emptyState ? (
        <Grid container item xs={12} justifyContent={"center"} alignItems={"center"}>
          <KonanEmptyState
            title={emptyState?.title ?? `${title} ${t("boxPlot.emptyState.unavailable")}`}
            subTitle={emptyState?.subTitle}
          />
        </Grid>
      ) : (
        <Grid container gap={1}>
          <Grid item xs={12}>
            {subHeader}
          </Grid>
          <Grid
            container
            item
            xs={12}
            style={{ height: graphHeight, padding: "8px 0px 0px", position: "relative" }}
            justifyContent={"center"}
            alignItems={"center"}
          >
            {adjustedData.length === 0 ? (
              <KonanEmptyState
                title={t("boxPlot.emptyState.couldntPlot", { title })}
                subTitle={t("boxPlot.emptyState.selectOtherFilters")}
              />
            ) : (
              <div className={styles.root}>
                {/* Fixed Y-axis container */}
                <div className={styles.yAxis}>
                  <ResponsiveBoxPlot
                    data={adjustedData}
                    theme={NivoTheme(theme)}
                    margin={{ top: MARGIN_TOP, right: MARGIN_RIGHT, bottom: MARGIN_BOTTOM, left: 50 }}
                    minValue={"auto"}
                    maxValue={"auto"}
                    subGroupBy="subgroup"
                    padding={0.2}
                    innerPadding={12}
                    enableGridX={false}
                    enableGridY={true}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={null}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legendOffset: -40,
                      truncateTickAt: 0,
                      format: (tick) => annotateNumber(tick, 1),
                    }}
                    colors={["transparent"]} // Make boxes transparent - we only want the axis
                    borderWidth={0}
                    medianWidth={0}
                    whiskerEndSize={0}
                    isInteractive={false}
                  />
                </div>

                {/* Scrollable chart area */}
                <div className={styles.chartAreaContainer}>
                  {/* Left scroll arrow */}
                  {showLeftArrow && (
                    <div
                      className={styles.scrollButton}
                      onClick={scrollLeft}
                      onMouseDown={() => startContinuousScroll("left")}
                      onMouseUp={stopContinuousScroll}
                      onMouseLeave={stopContinuousScroll}
                      data-direction="left"
                    >
                      <ChevronLeft fontSize="small" sx={{ color: theme.palette.secondary.icon.default }} />
                    </div>
                  )}

                  {/* Right scroll arrow */}
                  {showRightArrow && (
                    <div
                      className={styles.scrollButton}
                      onClick={scrollRight}
                      onMouseDown={() => startContinuousScroll("right")}
                      onMouseUp={stopContinuousScroll}
                      onMouseLeave={stopContinuousScroll}
                    >
                      <ChevronRight fontSize="small" sx={{ color: theme.palette.secondary.icon.default }} />
                    </div>
                  )}

                  {/* Scrollable chart container */}
                  <div
                    ref={scrollContainerRef}
                    className={styles.scrollableChartContainer}
                    onMouseMove={handleMouseMove}
                  >
                    <div
                      style={{
                        width: scrollable ? `${chartWidth}px` : "100%",
                        height: "100%",
                        minWidth: "100%",
                      }}
                    >
                      <ResponsiveBoxPlot
                        data={adjustedData}
                        theme={NivoTheme(theme)}
                        margin={{ top: MARGIN_TOP, right: MARGIN_RIGHT, bottom: MARGIN_BOTTOM, left: 0 }}
                        minValue={"auto"}
                        maxValue={"auto"}
                        subGroupBy="subgroup"
                        padding={0.2}
                        innerPadding={12}
                        enableGridX={true}
                        enableGridY={true}
                        axisTop={null}
                        axisRight={null}
                        axisBottom={{
                          tickSize: 5,
                          tickPadding: 5,
                          tickRotation: 0,
                          legendOffset: 32,
                          truncateTickAt: 0,
                        }}
                        axisLeft={null} // No left axis since it's fixed separately
                        colors={customColorScheme()}
                        borderRadius={4}
                        borderWidth={2}
                        borderColor={{
                          from: "color",
                          modifiers: [["darker", 0.25]],
                        }}
                        medianWidth={2}
                        medianColor={{
                          from: "color",
                          modifiers: [["darker", 0.25]],
                        }}
                        whiskerEndSize={0.6}
                        whiskerColor={{
                          from: "color",
                          modifiers: [["darker", 0.25]],
                        }}
                        motionConfig="stiff"
                        tooltip={CustomTooltip}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Grid>
        </Grid>
      )}
    </ChartsContainer>
  )
}
